import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * Note: thumbnails are intentionally NOT shown inside the zoom dialog. Navigation
 * between zoomed media happens through the prev/next slider arrows (and swipe/scroll
 * on touch devices), not through a thumbnail strip.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} [counter] - Optional "current / total" slider counter element.
 * @property {HTMLButtonElement} [prevButton] - Optional slider "previous" button.
 * @property {HTMLButtonElement} [nextButton] - Optional slider "next" button.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media } = this.refs;
    const targetImage = media[index];

    const open = () => {
      dialog.showModal();
      targetImage?.scrollIntoView({ behavior: 'instant' });
      this.updateSliderState(index);
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectMedia(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to keep the slider
   * counter and prev/next button states in sync with whichever media is
   * currently most visible (e.g. after a swipe/scroll gesture).
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);

    this.loadHighResolutionImage(mostVisibleElement);
    this.updateSliderState(activeIndex);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(dialog);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Closes the dialog when the user clicks outside of the actual media content
   * (i.e. on the dialog's backdrop / empty area, not on the image/video/slider controls).
   *
   * Native <dialog> elements fire click events with event.target === dialog itself
   * when the click lands on the backdrop area, so we use that to detect "clicked outside".
   *
   * @param {MouseEvent} event - The click event.
   */
  handleBackdropClick(event) {
    if (event.target === this.refs.dialog) {
      this.close();
    }
  }

  /**
   * Slider "previous" control - moves to the media before the currently visible one.
   */
  async prev() {
    await this.navigate(-1);
  }

  /**
   * Slider "next" control - moves to the media after the currently visible one.
   */
  async next() {
    await this.navigate(1);
  }

  /**
   * Moves the slider by a relative offset (-1 for previous, 1 for next),
   * clamped to the available media range.
   * @param {number} direction - -1 or 1.
   */
  async navigate(direction) {
    const { media } = this.refs;
    if (!media || !media.length) return;

    const mostVisibleElement = await getMostVisibleElement(media);
    const currentIndex = media.indexOf(mostVisibleElement);
    const nextIndex = Math.min(Math.max(currentIndex + direction, 0), media.length - 1);

    if (nextIndex === currentIndex) return;

    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectMedia(nextIndex, { behavior });
  }

  /**
   * Scrolls to a specific media index and updates the slider UI to match.
   * @param {number} index - The index of the media to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectMedia(index, options = { behavior: 'smooth' }) {
    const { media } = this.refs;
    if (!media || !media.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= media.length) return;

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }

    this.updateSliderState(index);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  /**
   * Updates the slider counter text ("2 / 5") and disables the prev/next
   * buttons when at the start/end of the media list.
   * @param {number} index - The currently active media index.
   */
  updateSliderState(index) {
    const { media, counter, prevButton, nextButton } = this.refs;
    if (!media || !media.length) return;

    if (counter) {
      counter.textContent = `${index + 1} / ${media.length}`;
    }

    if (prevButton) {
      prevButton.toggleAttribute('disabled', index <= 0);
    }

    if (nextButton) {
      nextButton.toggleAttribute('disabled', index >= media.length - 1);
    }
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
